// Vendor Libraries
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {get, map, startCase, uniq} from 'lodash';

// Core Libs
import downloadExcelFile from '../../lib/core/export';

// Components
import Button from '../core/button';
import PanelAction from '../core/panel-action';

// Configs
import exportConfig from '../../../config/export-config';
import recordDetailsConfig from '../../../config/record-details-config';

// Contexts
import {PanelContext} from '../../contexts/panel-context';
import {SearchContext} from '../../contexts/search-context';

// Styles
import './services-export-panel-action.scss';
import MassActionConfirm from '../search-data/mass-action-confirm';
import MassAction from '../../containers/mass-action';
import AsyncMediaItemsReportResultModal from '../search-data/async-media-items-report-result-modal';

class ServicesExportPanelAction extends Component {
    state = {
        childKey: null,
        isOverlayOpen: false,
        isResultOverlayOpen: false,
    };

    tempDefaultProps = {
        blacklistKeys: ['locked', 'lockedBy'],
        childKeys: [],
        customKeys: [],
        extendedDownload: false,
        filterByRoviFields: [],
        filterByTmsFields: [],
        keyOrder: [],
        parentKeys: [],
        priorityKeys: [],
        removeRoviFields: [],
        removeTmsFields: [],
        sectionName: '',
        whitelistKeys: []
    };

    getExportConfig(path, defaultValue) {
        const {exportConfig: exportConfigProp} = this.props;
        const {servicesDataKey} = this.context;

        return get(exportConfig[exportConfigProp || servicesDataKey], path, defaultValue);
    }

    getExportFilename = (panelContext) => {
        const {childKey} = this.state;
        const title = this.getTitle(panelContext);
        const timestamp = Date.now();

        return childKey ? `${title}-${startCase(childKey)}-${timestamp}.xlsx` : `${title}-${timestamp}.xlsx`;
    };

    getExportKeys() {
        const {childKey} = this.state;
        const {whitelistKeys = []} = this.props;
        const keyOrder = uniq([...whitelistKeys, ...this.getSearchKeyOrder()]);

        return {
            blacklistKeys: this.getKeys('blacklistKeys'),
            childKey,
            keyOrder,
            parentKeys: childKey ? this.getKeys('parentKeys') : undefined,
            priorityKeys: this.getKeys('priorityKeys'),
            whitelistKeys: this.getKeys('whitelistKeys'),
            customKeys: this.getKeys('customKeys'),
        };
    }

    getKeys(keyType) {
        const overriddenProps = {...this.tempDefaultProps, ...this.props};
        const {[keyType]: propKeys} = overriddenProps;

        return [...propKeys, ...this.getExportConfig(keyType, [])];
    }

    getPanelActionProps = (panelContext) => {
        const childKeys = this.getKeys('childKeys');

        return {
            contentRenderer: childKeys.length ? () => this.renderForm(panelContext) : undefined,
            onClick: !childKeys.length ? (event) => this.handleExportClick(event, panelContext) : undefined,
            hotkey: 'e',
            icon: 'file-o',
            title: 'Export Data',
            weight: -10,
        };
    };

    getSearchContent() {
        const {searchData} = this.props;

        return searchData || [];
    }

    getSearchKeyOrder() {
        const keyOrder = this.getExportConfig('keyOrder');
        const exportKeyOrder = (keyOrder && get(recordDetailsConfig, ['keyOrders', keyOrder])) || [];

        return [...this.getKeys('keyOrder'), ...exportKeyOrder];
    }

    getTitle = (panelContext) => {
        const {getPanelTitle} = panelContext;
        const {servicesDataKey} = this.context;
        let title;

        if (getPanelTitle) {
            title = getPanelTitle();
        }

        if (!title) {
            title = startCase(servicesDataKey);
        }

        return title || 'Data Export';
    };

    getMassActionOverlayProps = (massActionCtx) => {
        const {totalElements: actionsCount} = this.props;
        const {servicesDataKey} = this.context;
        const {childKey} = this.state;

        const {massActionCreateAsyncMediaItemReport, massActionCreateAsyncMediaItemDuplicateTitlesReport} =            massActionCtx;
        const handleSubmit = () => {
            this.handleMediaItemsResultOverlayOpen(true)();
            if (childKey !== 'duplicate titles') {
                massActionCreateAsyncMediaItemReport(servicesDataKey);
            } else {
                massActionCreateAsyncMediaItemDuplicateTitlesReport(servicesDataKey);
            }
            this.handleMediaItemsOverlayOpen(false)();
        };
        return {
            actionsCount: childKey === 'duplicate titles' ? undefined : actionsCount,
            label: childKey === 'duplicate titles' ? 'Generate Duplicate Titles Report' : 'Generate Media Items Report',
            onReset: this.handleMediaItemsOverlayOpen(false),
            onSubmit: handleSubmit,
        };
    };

    handleChildSelectChange = (event) => {
        const {onIsDuplicateDataReport, onFvodOrderingChange} = this.props;
        this.setState({
            childKey: event.target.value !== '' ? event.target.value : null,
        });

        onIsDuplicateDataReport && onIsDuplicateDataReport(event.target.value === 'duplicate titles');
        onFvodOrderingChange && onFvodOrderingChange(event.target.value === 'FVOD Ordering');
    };

    handleExportClick = (event, panelContext, filterBy = [], removeField = [], sectionName = null) => {
        event && event.preventDefault && event.preventDefault();

        let searchContent = JSON.parse(JSON.stringify(this.getSearchContent()));

        if (filterBy.length) {
            filterBy.forEach((filter) => {
                searchContent = searchContent.filter((content) => {
                    const filteredTop = content[filter] && !content[filter].toString().length;
                    const filteredAdditional = !filteredTop;
                    content[sectionName] && content[sectionName].length && content[sectionName][0][filter]
                        ? content[sectionName][0][filter] && content[sectionName][0][filter].toString().length
                        : null;
                    return filteredAdditional || filteredTop;
                });
            });
        }

        if (removeField.length) {
            removeField.forEach((toRemove) => {
                searchContent = searchContent.map((content) => {
                    if (content[toRemove]) {
                        content[toRemove] = undefined;
                    }
                    if (content[sectionName] && content[sectionName].length && content[sectionName][0][toRemove]) {
                        content[sectionName][0][toRemove] = undefined;
                    }
                    return content;
                });
            });
        }

        const {username} = this.props;

        if (searchContent.length) {
            downloadExcelFile(searchContent, this.getExportKeys(), this.getExportFilename(panelContext), username);
        }
    };

    handleMediaItemsOverlayOpen = (open) => (event) => {
        event && event.preventDefault && event.preventDefault();
        this.setState((prev) => ({...prev, isOverlayOpen: open}));
    };

    handleMediaItemsResultOverlayOpen = (open) => (event) => {
        event && event.preventDefault && event.preventDefault();
        this.setState((prev) => ({...prev, isResultOverlayOpen: open}));
    };

    scrollIntoView = () => {
        if (this.form) {
            this.form.scrollIntoView();
        }
    };

    renderDownloadButtons = (panelContext, extendedDownload) => {
        const {
            enableReportButton,
            filterByTmsFields = [],
            filterByRoviFields = [],
            onCancelOrder,
            onRefreshFvodData,
            onSaveOrder,
            removeRoviFields = [],
            removeTmsFields = [],
            sectionName = '',
        } = this.props;
        const {childKey} = this.state;
        return extendedDownload ? (
            <div className="services-export-panel-action--download-div">
                <Button
                    className="services-export-panel-action--download"
                    onClick={(event) => this.handleExportClick(event, panelContext)}
                    onLoad={setTimeout(this.scrollIntoView, 1)}
                >
                    Download [All]
                </Button>
                <Button
                    className="services-export-panel-action--download"
                    onClick={(event) => this.handleExportClick(event, panelContext, filterByTmsFields, removeTmsFields, sectionName)}
                    onLoad={setTimeout(this.scrollIntoView, 1)}
                >
                    Download [With TMS ID]
                </Button>
                <Button
                    className="services-export-panel-action--download"
                    onClick={(event) => this.handleExportClick(event, panelContext, filterByRoviFields, removeRoviFields, sectionName)}
                    onLoad={setTimeout(this.scrollIntoView, 1)}
                >
                    Download [With Rovi ID]
                </Button>
            </div>
        ) : childKey === 'FVOD Ordering' ? (
            <div className="services-export-panel-action--download-div">
                <Button
                    className="services-export-panel-action--download"
                    onClick={onSaveOrder}
                    onLoad={setTimeout(this.scrollIntoView, 1)}
                >
                    Save Order
                </Button>
                <Button
                    className="services-export-panel-action--download"
                    onClick={onCancelOrder}
                >
                    Cancel
                </Button>
                <Button
                    className="services-export-panel-action--download"
                    onClick={onRefreshFvodData}
                >
                    Refresh
                </Button>
            </div>
        ) : (
            <div className="services-export-panel-action--download-div">
                {childKey !== 'duplicate titles' && (
                    <Button
                        className="services-export-panel-action--download"
                        onClick={(event) => this.handleExportClick(event, panelContext)}
                        onLoad={setTimeout(this.scrollIntoView, 1)}
                    >
                        Download
                    </Button>
                )}
                {this.getTitle(panelContext).toLowerCase().includes('media item') && (
                    <Button
                        className="services-export-panel-action--download"
                        disabled={enableReportButton}
                        onClick={this.handleMediaItemsOverlayOpen(true)}
                        onLoad={setTimeout(this.scrollIntoView, 1)}
                    >
                        Generate Report...
                    </Button>
                )}
            </div>
        );
    };

    renderForm = (panelContext) => {
        const {extendedDownload = false} = this.props;
        const {childKey} = this.state;
        return (
            <div className="services-export-panel-action--form" ref={(form) => (this.form = form)}>
                <label>Export Data</label>
                <select
                    className="services-export-panel-action--select"
                    onChange={this.handleChildSelectChange}
                    value={childKey || ''}
                >
                    <option value="">{this.getTitle(panelContext)}</option>
                    {map(this.getKeys('childKeys'), (childKey) => (
                        <option key={childKey} value={childKey}>
                            {startCase(childKey)}
                        </option>
                    ))}
                </select>
                {this.renderDownloadButtons(panelContext, extendedDownload)}
            </div>
        );
    };

    renderMassActionModal = (massActionContext) => {
        const {isOverlayOpen} = this.state;
        if (!isOverlayOpen) {
            return null;
        }
        return <MassActionConfirm {...this.getMassActionOverlayProps(massActionContext)} />;
    };

    render() {
        const {servicesDataKey} = this.context;
        const {childKey, isResultOverlayOpen} = this.state;
        return (
            <>
                <PanelContext.Consumer>
                    {(panelContext) => <PanelAction {...this.getPanelActionProps(panelContext)} />}
                </PanelContext.Consumer>
                <MassAction massActionData={{active: true}} massActionName={servicesDataKey}>
                    {(massAction) => this.renderMassActionModal(massAction)}
                </MassAction>
                <AsyncMediaItemsReportResultModal
                    isDuplicateReport={childKey === 'duplicate titles'}
                    isOpen={isResultOverlayOpen}
                    onReset={this.handleMediaItemsResultOverlayOpen(false)}
                />
            </>
        );
    }
}

ServicesExportPanelAction.propTypes = {
    blacklistKeys: PropTypes.arrayOf(PropTypes.string),
    childKeys: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    customKeys: PropTypes.arrayOf(PropTypes.object),
    enableReportButton: PropTypes.bool,
    exportConfig: PropTypes.string,
    extendedDownload: PropTypes.bool,
    filterByRoviFields: PropTypes.arrayOf(PropTypes.string),
    filterByTmsFields: PropTypes.arrayOf(PropTypes.string),
    keyOrder: PropTypes.array,
    onCancelOrder: PropTypes.func,
    onFvodOrderingChange: PropTypes.func,
    onIsDuplicateDataReport: PropTypes.func,
    onRefreshFvodData: PropTypes.func,
    onSaveOrder: PropTypes.func,
    parentKeys: PropTypes.arrayOf(PropTypes.string),
    priorityKeys: PropTypes.arrayOf(PropTypes.string),
    removeRoviFields: PropTypes.arrayOf(PropTypes.string),
    removeTmsFields: PropTypes.arrayOf(PropTypes.string),
    searchData: PropTypes.array,
    sectionName: PropTypes.string,
    totalElements: PropTypes.number,
    username: PropTypes.string,
    whitelistKeys: PropTypes.arrayOf(PropTypes.string),
};

ServicesExportPanelAction.contextType = SearchContext;

const mapStateToProps = (state) => ({
    username: state.auth.user,
});

export default connect(mapStateToProps, null)(ServicesExportPanelAction);
